import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Input } from "reactstrap";

const SEARCH_OPTIONS = {
  followingNumber: {
    label: "Correlativo",
    placeholder: "Ej: 554012",
    icon: "🔢"
  },
  order_id: {
    label: "N° Venta", 
    placeholder: "Ej: ABCD123456",
    icon: "🛒"
  },
  "shipping_address.address_line": {
    label: "Dirección",
    placeholder: "Ej: Providencia 111",
    icon: "📍"
  }
}

// Modo consolidado: un solo input que busca en correlativo + N° venta + dirección
// a la vez (la vista resuelve el OR lanzando las 3 queries). Sin selector de tipo.
const CONSOLIDATED_OPTION = {
  label: "Buscar",
  placeholder: "Correlativo, N° venta o dirección...",
  icon: "🔍"
}

export default function SearchBar({
  message,
  onChange,
  loading = false,
  searchButton = false,
  onSearchButton = () => {},
  className = "",
  showSpinner = true, // Nuevo prop para controlar si mostrar el spinner
  size = "md",
  consolidated = false, // un solo campo que consolida los 3 tipos de búsqueda
  value // valor externo: refleja el filtro YA aplicado (p. ej. al re-montar tras volver del detalle)
}) {
  const { t } = useTranslation();
  const [queryType, setQueryType] = useState(consolidated ? "consolidated" : "followingNumber");
  const [query, setQuery] = useState(value ?? "");

  // Sincroniza el input cuando el filtro aplicado cambia desde fuera
  // (al re-montar tras "Volver" o al limpiarse). Sin esto, el campo quedaba
  // vacío mientras el filtro seguía aplicado: confuso e imposible de borrar.
  useEffect(() => {
    if (value !== undefined) {
      setQuery(value);
    }
  }, [value]);
  const [isFocused, setIsFocused] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (onChange) {
      onChange(query, queryType);
    }
  }, [queryType, query]);

  // Cerrar dropdown al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  const handleSearch = () => {
    if (searchButton && onSearchButton) {
      onSearchButton();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleQueryTypeChange = (newQueryType) => {
    setQueryType(newQueryType);
    setShowDropdown(false);
    // Enfocar el input después de cambiar el tipo
    setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
  };

  const currentOption = consolidated ? CONSOLIDATED_OPTION : SEARCH_OPTIONS[queryType];

  return (
    <div className={`mobile-search-bar ${consolidated ? 'mobile-search-bar--consolidated' : ''} ${className}`}>
      {/* Mobile Layout: Vertical Stack */}
      <div className="mobile-search-bar__container">
        {/* Selector de tipo (oculto en modo consolidado) */}
        {!consolidated && (
          <div className="mobile-search-bar__type-wrapper" ref={dropdownRef}>
            <button
              className="mobile-search-bar__type-button"
              onClick={() => setShowDropdown(!showDropdown)}
              disabled={loading}
              type="button"
            >
              <span className="mobile-search-bar__type-icon">{currentOption.icon}</span>
              <span className="mobile-search-bar__type-label">{t(currentOption.label)}</span>
              <span className={`mobile-search-bar__type-arrow ${showDropdown ? 'mobile-search-bar__type-arrow--open' : ''}`}>
                ▼
              </span>
            </button>

            {showDropdown && (
              <div className="mobile-search-bar__dropdown">
                {Object.entries(SEARCH_OPTIONS).map(([value, option]) => (
                  <button
                    key={value}
                    className={`mobile-search-bar__dropdown-item ${queryType === value ? 'mobile-search-bar__dropdown-item--active' : ''}`}
                    onClick={() => handleQueryTypeChange(value)}
                    type="button"
                  >
                    <span className="mobile-search-bar__dropdown-icon">{option.icon}</span>
                    <span className="mobile-search-bar__dropdown-label">{t(option.label)}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Input y botón - Horizontal en mobile */}
        <div className="mobile-search-bar__input-wrapper">
          <Input
            ref={inputRef}
            className={`mobile-search-bar__input ${isFocused ? 'mobile-search-bar__input--focused' : ''} ${loading ? 'mobile-search-bar__input--loading' : ''}`}
            placeholder={message || t(currentOption.placeholder)}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            onKeyDown={handleKeyDown}
            disabled={loading}
            bsSize={size}
          />
          
          {/* Botón de búsqueda - Tamaño fijo y compacto */}
          {searchButton && (
            <button
              className="mobile-search-bar__search-button"
              onClick={handleSearch}
              disabled={loading}
              type="button"
              title={t("Buscar")}
            >
              {loading && showSpinner ? (
                <div className="mobile-search-bar__spinner"></div>
              ) : (
                <svg className="mobile-search-bar__search-icon" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
                </svg>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}